import { useState, useEffect } from 'react'

interface CaptchaProps {
  onVerify: (isValid: boolean) => void
  required?: boolean
}

export default function Captcha({ onVerify, required = true }: CaptchaProps) {
  const [isVerified, setIsVerified] = useState(false)
  const [challenge, setChallenge] = useState('')
  const [userAnswer, setUserAnswer] = useState('')
  const [error, setError] = useState('')

  // Simple math challenge generation
  const generateChallenge = () => {
    const operations = ['+', '-', '*']
    const operation = operations[Math.floor(Math.random() * operations.length)]
    let num1 = Math.floor(Math.random() * 10) + 1
    let num2 = Math.floor(Math.random() * 10) + 1
    let answer: number

    switch (operation) {
      case '+':
        answer = num1 + num2
        break
      case '-':
        // Ensure positive result
        if (num1 < num2) [num1, num2] = [num2, num1]
        answer = num1 - num2
        break
      case '*':
        // Keep numbers smaller for easier calculation
        num1 = Math.floor(Math.random() * 5) + 1
        num2 = Math.floor(Math.random() * 5) + 1
        answer = num1 * num2
        break
      default:
        answer = num1 + num2
    }

    const challengeText = `${num1} ${operation} ${num2} = ?`
    setChallenge(challengeText)
    setUserAnswer('')
    setError('')
    setIsVerified(false)
    onVerify(false)
    
    // Store the answer for verification
    ;(window as any).captchaAnswer = answer
  }

  useEffect(() => {
    generateChallenge()
  }, [])

  const handleVerify = () => {
    const correctAnswer = (window as any).captchaAnswer
    const userNum = parseInt(userAnswer.trim())
    
    if (isNaN(userNum)) {
      setError('Please enter a valid number')
      setIsVerified(false)
      onVerify(false)
      return
    }

    if (userNum === correctAnswer) {
      setError('')
      setIsVerified(true)
      onVerify(true)
    } else {
      setError('Incorrect answer. Please try again.')
      setIsVerified(false)
      onVerify(false)
      generateChallenge() // Generate new challenge on wrong answer
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserAnswer(e.target.value)
    setError('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerify()
    }
  }

  return (
    <div className="space-y-3">
      <div className="bg-gray-50 p-4 rounded-lg border">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-700">
            Security Verification
          </label>
          <button
            type="button"
            onClick={generateChallenge}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            New Challenge
          </button>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <div className="text-lg font-mono text-center bg-white p-3 rounded border mb-2">
              {challenge}
            </div>
            <input
              type="text"
              value={userAnswer}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Your answer"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required={required}
            />
          </div>
          <button
            type="button"
            onClick={handleVerify}
            disabled={!userAnswer.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Verify
          </button>
        </div>
        
        {error && (
          <p className="text-red-600 text-sm mt-2">{error}</p>
        )}
        
        {isVerified && (
          <div className="flex items-center text-green-600 text-sm mt-2">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Verified successfully
          </div>
        )}
      </div>
      
      <p className="text-xs text-gray-500">
        This helps us prevent automated registrations and keep our community safe.
      </p>
    </div>
  )
}
